<template>
  <template v-if="status === 'loading'">
    <div class="szw-flex szw-items-center szw-justify-center szw-space-x-[10px] szw-pt-[6px] szw-px-[8px]">
      <div class="szw-flex-grow szw-h-px szw-bg-border"></div>
      <div class="szw-bg-section-static szw-w-[100px] szw-h-[14px]"></div>
      <div class="szw-flex-grow szw-h-px szw-bg-border"></div>
    </div>
  </template>
  <template v-else-if="hasScorers">
    <!-- title -->
    <div class="szw-flex szw-items-center szw-justify-center szw-space-x-[10px] szw-pt-[6px] szw-px-[8px]">
      <div class="szw-flex-grow szw-h-px szw-bg-border"></div>
      <div class="szw-text-center szw-font-light szw-text-text-icon-tertiary szw-uppercase szw-tracking-wider" :class="[widgetSize.font]">{{ loc('sm_goal_scorers', langId) }}</div>
      <div class="szw-flex-grow szw-h-px szw-bg-border"></div>
    </div>

    <!-- list -->
    <div class="szw-flex szw-flex-row szw-items-start szw-py-[4px] szw-gap-[2px] szw-px-[8px] szw-justify-center szw-text-text-icon-primary">
      <!-- Home Scorers (Left Side) -->
      <div class="szw-flex szw-flex-1 szw-flex-col szw-justify-center szw-gap-[4px]">
        <div v-for="player in homeScorers" :key="player.id" class="szw-flex szw-flex-row szw-items-center szw-gap-[4px]">
          <span class="szw-font-medium szw-uppercase szw-tracking-[0.04em]" :class="[widgetSize.font]">{{ player.matchTime }}'</span>
          <span v-if="player?.subType === 'own_goal'" class="szw-font-semibold szw-uppercase szw-tracking-[0.04em]" :class="[widgetSize.font]">(OG)</span>
          <span class="szw-font-light szw-uppercase szw-tracking-[0.04em]" :class="[widgetSize.font]">{{ formatPlayerName(player.name) }}</span>
        </div>
      </div>

      <!-- Away Scorers (Right Side) -->
      <div class="szw-flex szw-flex-1 szw-flex-col szw-items-end szw-justify-center szw-gap-[4px]">
        <div v-for="player in awayScorers" :key="player.id" class="szw-flex szw-flex-row szw-items-center szw-gap-[4px]">
          <span class="szw-font-light szw-uppercase szw-tracking-[0.04em] szw-text-right" :class="[widgetSize.font]">{{ formatPlayerName(player.name) }}</span>
          <span v-if="player?.subType === 'own_goal'" class="szw-font-semibold szw-uppercase szw-tracking-[0.04em]" :class="[widgetSize.font]">(OG)</span>
          <span class="szw-font-medium szw-uppercase szw-tracking-[0.04em]" :class="[widgetSize.font]">{{ player.matchTime }}'</span>
        </div>
      </div>
    </div>
  </template>
</template>

<script setup>
import { inject, computed } from 'vue'

const props = defineProps({
  scorers: {
    type: Array,
    default: () => []
  },
  size: {
    type: Number,
    default: 320
  }
})

const status = inject('status')
const langId = inject('langId', '')

const getScorersByQualifier = qualifier => {
  if (!Array.isArray(props.scorers)) {
    return [] // Return an empty array if props.scorers is not valid
  }

  return props.scorers
    .filter(event => event.qualifier === qualifier)
    .flatMap(event => {
      // Check if players array exists and has at least one player
      if (!Array.isArray(event.players) || event.players.length === 0) {
        return [] // Return an empty array if no players are found
      }

      return event.players
        .filter(player => player.type === 'scorer')
        .map(player => ({
          ...player,
          matchTime: event.matchTime?.total?.time || event.matchTime?.regular?.time,
          subType: event.subType
        }))
    })
}

const homeScorers = computed(() => getScorersByQualifier('home'))
const awayScorers = computed(() => getScorersByQualifier('away'))

const hasScorers = computed(() => homeScorers.value.length + awayScorers.value.length > 0)

const formatPlayerName = fullName => {
  const nameParts = fullName.split(' ')
  return nameParts.length === 1 ? nameParts[0] : `${nameParts[0].charAt(0)}. ${nameParts.slice(1).join(' ')}`
}

const widgetSize = computed(() => {
  const fontSizes = {
    s: 'szw-text-[11px] szw-leading-[12px]',
    m: 'szw-text-[14px] szw-leading-[17px]',
    l: 'szw-text-[16px] szw-leading-[20px]'
  }

  let conf

  if (props.size <= 480) {
    conf = { font: fontSizes.s }
  } else if (props.size > 480 && props.size < 640) {
    conf = { font: fontSizes.m }
  } else if (props.size > 640 && props.size < 768) {
    conf = { font: fontSizes.m }
  } else {
    conf = { font: fontSizes.l }
  }

  return conf
})
</script>
